import { useEffect, useId, useRef, useState } from 'react';
import {
  usePortal,
  useAnimationState,
  useBodyScrollLock,
  useEscapeKey,
  useFocusTrap,
  useInitialFocus,
  useClickOutside,
} from '@/shared/hooks';
import { MODAL_DEFAULT_Z_INDEX, registerModal, unregisterModal } from '@/shared/utils/modalStack';

interface UseModalOptions {
  isOpen: boolean;
  onClose: () => void;
  zIndex?: number;
  disableEscapeClose?: boolean;
  disableOutsideTab?: boolean;
  disableOutsideClick?: boolean;
}

interface UseModalReturn {
  isVisible: boolean;
  animateIn: boolean;
  container: HTMLDivElement | null;
  modalRef: React.RefObject<HTMLDivElement>;
  effectiveZIndex: number;
}

export const useModal = ({
  isOpen,
  onClose,
  zIndex = MODAL_DEFAULT_Z_INDEX,
  disableEscapeClose = false,
  disableOutsideTab = false,
  disableOutsideClick = false,
}: UseModalOptions): UseModalReturn => {
  const modalInstanceId = useId();
  const [effectiveZIndex, setEffectiveZIndex] = useState(zIndex);

  // Hooks reutilizables
  const container = usePortal();
  const { isVisible, isAnimating: animateIn } = useAnimationState(isOpen, { exitDelay: 200 });
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      unregisterModal(modalInstanceId);
      setEffectiveZIndex(zIndex);
      return;
    }

    setEffectiveZIndex(registerModal(modalInstanceId, zIndex));

    return () => {
      unregisterModal(modalInstanceId);
    };
  }, [isOpen, modalInstanceId, zIndex]);

  // Bloquear scroll del body
  useBodyScrollLock(isOpen);

  // Manejar tecla Escape
  useEscapeKey(isOpen, onClose, disableEscapeClose);

  // Focus inicial
  useInitialFocus(isOpen, modalRef);

  // Focus trap
  useFocusTrap(isOpen, modalRef, !disableOutsideTab);

  // Manejar clic fuera del modal
  useClickOutside(isOpen, modalRef, onClose, disableOutsideClick);

  return {
    isVisible,
    animateIn,
    container,
    modalRef,
    effectiveZIndex,
  };
};
